import type { PaymentMethodId, UserPaymentAttempt } from "@/features/user/types"

type CreateUserPaymentAttemptParams = {
  billId: number
  orderIds: number[]
  paymentMethod: PaymentMethodId
  tipPercentage: number
  // When set, a fixed custom tip amount is sent and the backend ignores the
  // percentage. When undefined, the percentage-based calculation is used.
  tipAmount?: number
  // Set when paying a share of a split. The backend recomputes the amount from
  // the split's live balance, so nothing about the figure travels from here.
  splitId?: number
  returnUrl: string
  idempotencyKey: string
}

type CreateUserPaymentAttemptSuccess = {
  ok: true
  checkoutUrl: string | null
  paymentAttempt: UserPaymentAttempt
}

type CreateUserPaymentAttemptFailure = {
  ok: false
  errors: string[]
}

export type CreateUserPaymentAttemptResult =
  | CreateUserPaymentAttemptSuccess
  | CreateUserPaymentAttemptFailure

export async function createUserPaymentAttempt({
  billId,
  orderIds,
  paymentMethod,
  tipPercentage,
  tipAmount,
  splitId,
  returnUrl,
  idempotencyKey,
}: CreateUserPaymentAttemptParams): Promise<CreateUserPaymentAttemptResult> {
  const response = await fetch(`/bills/${billId}/payment_attempts`, {
    method: "POST",
    credentials: "same-origin",
    headers: {
      Accept: "application/json",
      "Content-Type": "application/json",
      "X-CSRF-Token": csrfToken(),
      "X-Requested-With": "XMLHttpRequest",
    },
    body: JSON.stringify({
      order_ids: orderIds,
      payment_method: paymentMethod,
      tip_percentage: tipPercentage,
      ...(tipAmount === undefined ? {} : { tip_amount: tipAmount }),
      ...(splitId === undefined ? {} : { split_id: splitId }),
      return_url: returnUrl,
      idempotency_key: idempotencyKey,
    }),
  })

  const payload = await parseJson(response)

  if (!response.ok) {
    return {
      ok: false,
      errors: errorsFrom(payload, response.statusText),
    }
  }

  return {
    ok: true,
    checkoutUrl: typeof payload.checkoutUrl === "string" ? payload.checkoutUrl : null,
    paymentAttempt: payload.paymentAttempt as UserPaymentAttempt,
  }
}

function csrfToken() {
  return document.querySelector<HTMLMetaElement>('meta[name="csrf-token"]')?.content ?? ""
}

async function parseJson(response: Response) {
  try {
    return (await response.json()) as Record<string, unknown>
  } catch {
    return {}
  }
}

function errorsFrom(payload: Record<string, unknown>, fallback: string) {
  if (Array.isArray(payload.errors)) {
    return payload.errors.map(String)
  }

  if (typeof payload.error === "string") {
    return [payload.error]
  }

  return [fallback]
}
